import { forEach } from 'lodash';

import ScheduleDataModel from '../../schedules/ScheduleDataModel';

import CoreUtils from '../../common/utils/CoreUtils';

import EnergyValue from '../../common/value-types/EnergyValue';
import MetadataHelper from '../../common/value-types/MetadataHelper';

import BaseFlexShapeValidationInfoRenderer from './BaseFlexShapeValidationInfoRenderer';

export default class BaseFlexShapeDataModel extends ScheduleDataModel {
  constructor(locale, counterparty, commandDataModel) {
    super(locale, counterparty, commandDataModel)
    
    this.activateEnergyElection = false;
    this.baseElectionInEffect = false;

    this.baseContract = null;
    this.flexContract = null;
    this.shapeContract = null;
  }

  processContracts(scheduleUpdateDto) {
    this.baseContract = this.assignContract(scheduleUpdateDto.baseContract);
    this.flexContract = this.assignContract(scheduleUpdateDto.flexContract);
    this.shapeContract = this.assignContract(scheduleUpdateDto.shapeContract);
  }

  assignContract(contractDto) {
    if (CoreUtils.isUndefinedOrNull(contractDto))
      return null;

    var contract = {};
    Object.assign(contract, contractDto);

    this.registerContract(contract);

    return contract;
  }

  getDefaultSortCriteria() {
    return {
      fields: ['localDateMs', 'contractOrder'],
      order: ['asc', 'asc']
    };
  }

  updateOverallStatus() {
    super.updateOverallStatus();

    this.hasValidationErrors = this.allSchedules.some(
      schedule => schedule.hasInvalidBlockSizes
    );

    if (CoreUtils.isDefinedAndNotNull(this.commandDataModel)) {
      this.commandDataModel.updateProperties({
        hasValidationErrors: this.hasValidationErrors
      });
    }
  }

  onScheduleCreated(schedule) {
    super.onScheduleCreated(schedule);

    schedule.contractOrder = this.getContractSortOrder(schedule);

    this.validateScheduleValues(schedule, []);
  }

  onScheduleUpdated(schedule, isServerUpdate, isPeerUpdate, updatedDataFields, quickEntryHints) {
    super.onScheduleUpdated(schedule, isServerUpdate, isPeerUpdate, updatedDataFields, quickEntryHints);

    if (CoreUtils.isDefinedAndNotNull(this.flexContract)
      && CoreUtils.isDefinedAndNotNull(this.shapeContract)
      && this.indexedContracts[schedule.contractCode] === this.flexContract)
      this.correctShapeAfterFlexChanges(schedule, isServerUpdate, isPeerUpdate, updatedDataFields);

    this.validateScheduleValues(schedule, updatedDataFields);
  }

  canSelectRow(scheduleId) {
    if (CoreUtils.isUndefinedOrNull(this.baseContract))
      return false;

    if (CoreUtils.isUndefinedOrNull(scheduleId) || typeof scheduleId !== 'string')
      return false;

    var schedule = this.indexedSchedules[scheduleId];
    if (CoreUtils.isUndefinedOrNull(schedule))
      return false;

    return this.indexedContracts[schedule.contractCode] === this.baseContract;
  }

  canModifyFieldValue(schedule, dataField) {
    return super.canModifyFieldValue(schedule, dataField) && this.activateEnergyElection;
  }

  canEditFieldValue(schedule, dataField) {
    if (!super.canEditFieldValue(schedule, dataField))
      return false;

    if (!this.activateEnergyElection)
      return false;

    if (this.indexedContracts[schedule.contractCode] === this.baseContract)
      return false;

    var baseSchedule = this.getRelatedSchedule(schedule, this.baseContract);
    if (CoreUtils.isUndefinedOrNull(baseSchedule))
      return false;

    return baseSchedule[dataField].validateMinimum(
      new EnergyValue(this.baseContract.hourlyEnergyMaximum)
    );
  }

  onFieldValuesEditing(schedule, dataFieldValues) {
    if (!super.onFieldValuesEditing(schedule, dataFieldValues))
      return false;

    this.filterUpdatingFlex(schedule, dataFieldValues);
    this.filterUpdatingShape(schedule, dataFieldValues);

    return true;
  }

  updateScheduleUsage(schedule, updatedDataFields) {
    super.updateScheduleUsage(schedule, updatedDataFields);

    var totalMWh = 0;
    var totalH = 0;

    forEach(
      schedule.hourlyDataFields, dataField => {
        totalMWh += schedule[dataField].averageValue;
        totalH += schedule[dataField].averageDuration;
      }
    );

    if (totalMWh !== schedule.totalMWh) {
      schedule.totalMWh = totalMWh;
      updatedDataFields.push('totalMWh');
    }

    if (totalH !== schedule.totalH) {
      schedule.totalH = totalH;
      updatedDataFields.push('totalH');
    }
  }

  getContractSortOrder(schedule) {
    var contract = this.indexedContracts[schedule.contractCode];
    if (CoreUtils.isUndefinedOrNull(contract))
      return 3;

    switch (contract) {
      case this.baseContract: return 0;
      case this.flexContract: return 1;
      case this.shapeContract: return 2;
      default: return 3;
    }
  }

  validateScheduleValues(schedule, updatedDataFields) {
    schedule.hasInvalidBlockSizes = false;

    var contract = this.indexedContracts[schedule.contractCode];
    if (CoreUtils.isUndefinedOrNull(contract))
      return;

    var minimumContiguousBlockSize = parseInt(contract.parameters.MinimumContiguousBlockSize);
    if (minimumContiguousBlockSize <= 1)
      return;

    var electedEnergyValue = new EnergyValue(contract.hourlyEnergyMaximum);

    var fieldGroups = [];
    var currentFieldGroup = [];
    var previousValidationContentRender = {};

    schedule.hourlyDataFields.forEach(dataField => {
      var metadataField = MetadataHelper.resolveMetadataField(dataField);

      previousValidationContentRender[dataField] = schedule[metadataField].validationContentRender;
      schedule[metadataField].validationContentRender = null;

      if (schedule[dataField].validateMinimum(electedEnergyValue))
        currentFieldGroup.push(dataField);
      else if (currentFieldGroup.length > 0) {
        fieldGroups.push(currentFieldGroup);
        currentFieldGroup = [];
      }
    });

    if (currentFieldGroup.length > 0)
      fieldGroups.push(currentFieldGroup);

    fieldGroups.forEach(baseGroup => {
      if (baseGroup.length < minimumContiguousBlockSize) {
        baseGroup.forEach(dataField => {
          var metadataField = MetadataHelper.resolveMetadataField(dataField);

          if (schedule[metadataField].electionCutoffs.every(electionCutoff => !electionCutoff.isPastElectionCutoff)
            || schedule.enablePostCutoffChanges) {
            schedule.hasInvalidBlockSizes = true;
            schedule[metadataField].validationContentRender = () => BaseFlexShapeValidationInfoRenderer(
              contract.code,
              minimumContiguousBlockSize,
              this.locale
            );
          }
        });
      }
    });

    schedule.hourlyDataFields.forEach(dataField => {
      var metadataField = MetadataHelper.resolveMetadataField(dataField);

      if (CoreUtils.isDefinedAndNotNull(schedule[metadataField].validationContentRender) !== CoreUtils.isDefinedAndNotNull(previousValidationContentRender[dataField]))
        updatedDataFields.push(dataField);
    });
  }

  setActivateEnergyElection(activate) {
    this.activateEnergyElection = activate;
  }

  toggleBaseEnergyValues(scheduleDataFieldSets, addHours) {
    if (CoreUtils.isUndefinedOrNull(this.baseContract))
      return;

    var scheduleUpdateSets = [];
    scheduleDataFieldSets.forEach(scheduleDataFieldSet => {
      if (!scheduleDataFieldSet.scheduleId.includes(this.baseContract.code))
        return;

      var baseSchedule = this.indexedSchedules[scheduleDataFieldSet.scheduleId];
      if (CoreUtils.isUndefinedOrNull(baseSchedule))
        return;

      var updatedDataFields = scheduleDataFieldSet.dataFields.filter(
        dataField => baseSchedule.hourlyDataFields.indexOf(dataField) !== -1
      );

      var baseValue = new EnergyValue(
        addHours
          ? this.baseContract.hourlyEnergyMaximum
          : this.baseContract.hourlyEnergyMinimum
      );

      updatedDataFields = updatedDataFields.filter(
        dataField => !baseSchedule[dataField].equals(baseValue)
          && this.canModifyFieldValue(baseSchedule, dataField)
      );

      scheduleUpdateSets.push(
        this.generateSingleValueScheduleUpdateSet(
          scheduleDataFieldSet.scheduleId,
          updatedDataFields,
          baseValue
        )
      );

      if (CoreUtils.isUndefinedOrNull(this.flexContract))
        return;

      var flexValue = new EnergyValue(
        addHours
          ? this.flexContract.hourlyEnergyMaximum
          : this.flexContract.hourlyEnergyMinimum
      );

      scheduleUpdateSets.push(
        this.generateSingleValueScheduleUpdateSet(
          this.getRelatedScheduleId(
            scheduleDataFieldSet.scheduleId,
            this.baseContract.code,
            this.flexContract.code
          ),
          updatedDataFields,
          flexValue
        )
      );

      if (CoreUtils.isUndefinedOrNull(this.shapeContract))
        return;

      var shapeValue = new EnergyValue(
        addHours
          ? Math.min(
            this.flexContract.hourlyEnergyMaximum,
            this.shapeContract.hourlyEnergyMaximum
          )
          : Math.max(
            this.flexContract.hourlyEnergyMinimum,
            this.shapeContract.hourlyEnergyMinimum
          )
      );

      scheduleUpdateSets.push(
        this.generateSingleValueScheduleUpdateSet(
          this.getRelatedScheduleId(
            scheduleDataFieldSet.scheduleId,
            this.baseContract.code,
            this.shapeContract.code
          ),
          updatedDataFields,
          shapeValue
        )
      );
    });

    this.baseElectionInEffect = true;
    var updateCount = this.updateSchedules(scheduleUpdateSets, false);
    this.baseElectionInEffect = false;

    return updateCount;
  }

  filterUpdatingFlex(flexSchedule, updatingFlexValues) {
    if (CoreUtils.isUndefinedOrNull(this.flexContract)
      || this.indexedContracts[flexSchedule.contractCode] !== this.flexContract)
      return;

    var hourlyEnergyMinimum = new EnergyValue(this.flexContract.hourlyEnergyMinimum);
    var hourlyEnergyMaximum = new EnergyValue(this.flexContract.hourlyEnergyMaximum);

    CoreUtils.iterateProperties(updatingFlexValues, (dataField, flexValue) => {
      if (flexSchedule.hourlyDataFields.indexOf(dataField) === -1)
        return;

      if (flexValue === undefined)
        return;

      if (!(flexValue instanceof EnergyValue)
        || !flexValue.isCompatibleResolution(flexSchedule.subhourlyResolution)) {
        updatingFlexValues[dataField] = flexSchedule[dataField];
        return;
      }

      flexValue = flexValue.applyMinimum(
        hourlyEnergyMinimum,
        this.flexContract.subhourlyResolution
      );

      flexValue = flexValue.applyMaximum(
        hourlyEnergyMaximum,
        this.flexContract.subhourlyResolution
      );

      var isLockedOutMask = this.resolveIsLockedOutMask(flexSchedule, dataField);

      updatingFlexValues[dataField] = flexSchedule[dataField].overwrite(
        flexValue,
        isLockedOutMask.inverse(),
        this.flexContract.subhourlyResolution
      );
    });
  }

  correctShapeAfterFlexChanges(flexSchedule, isServerUpdate, isPeerUpdate, updatedDataFields) {
    if (CoreUtils.isUndefinedOrNull(this.shapeContract)
      || this.baseElectionInEffect)
      return;

    var shapeSchedule = this.getRelatedSchedule(flexSchedule, this.shapeContract);
    if (CoreUtils.isUndefinedOrNull(shapeSchedule))
      return;

    var shapeValues = {};
    updatedDataFields.forEach(dataField => {
      if (shapeSchedule.hourlyDataFields.indexOf(dataField) === -1)
        return;

      var flexValue = flexSchedule[dataField];
      var shapeValue = shapeSchedule[dataField];

      if (flexValue === undefined || shapeValue === undefined)
        return;

      var metadataField = MetadataHelper.resolveMetadataField(dataField);
      if ((isServerUpdate || isPeerUpdate) && shapeSchedule[metadataField].modifiedMask.none)
        return;

      shapeValues[dataField] = this.correctShapeValue(flexValue, shapeValue, this.shapeContract);
    });

    var scheduleUpdateSets = [{
      scheduleId: shapeSchedule.scheduleId,
      values: shapeValues
    }];

    this.updateSchedules(scheduleUpdateSets, false);
  }

  filterUpdatingShape(shapeSchedule, updatingShapeValues) {
    if (CoreUtils.isUndefinedOrNull(this.flexContract)
      || CoreUtils.isUndefinedOrNull(this.shapeContract)
      || this.indexedContracts[shapeSchedule.contractCode] !== this.shapeContract)
      return;

    var flexSchedule = this.getRelatedSchedule(shapeSchedule, this.flexContract);
    if (CoreUtils.isUndefinedOrNull(flexSchedule))
      return;

    CoreUtils.iterateProperties(updatingShapeValues, (dataField, shapeValue) => {
      if (shapeSchedule.hourlyDataFields.indexOf(dataField) === -1)
        return;

      var flexValue = flexSchedule[dataField];

      if (flexValue === undefined || shapeValue === undefined)
        return;

      shapeValue = this.correctShapeValue(flexValue, shapeValue, this.shapeContract);

      var isLockedOutMask = this.resolveIsLockedOutMask(shapeSchedule, dataField);

      updatingShapeValues[dataField] = shapeSchedule[dataField].overwrite(
        shapeValue,
        isLockedOutMask.inverse(),
        this.shapeContract.subhourlyResolution
      );
    });
  }

  correctShapeValue(flexValue, shapeValue, shapeContract) {
    if (!(shapeValue instanceof EnergyValue))
      return;

    shapeValue = shapeValue.applyMinimum(
      new EnergyValue(shapeContract.hourlyEnergyMinimum),
      shapeContract.subhourlyResolution
    );

    shapeValue = shapeValue.applyMaximum(
      flexValue,
      shapeContract.subhourlyResolution
    );

    return shapeValue;
  }
}